import React from 'react'
import { css } from '@emotion/react'
import useMedia from 'use-media'
import { bool, string, array, node, func, object, oneOfType } from 'prop-types'

import ChevronIcon from './ChevronIcon'
import { pushGenericEvent } from '@/utils/analytics'
import mediaQueries from '@/layout/media-queries'
import ChevronRight from '@/assets/icons/chevron-nav-right.inline.svg'

const chevronRightCss = (theme) => css`
  > path {
    fill: ${theme.colors.gray900};
    opacity: 1;
  }
`

const navLinkCss = (theme) => css`
  display: flex;
  align-items: center;
  font-family: ${theme.fontFamily};
  font-size: 16px;
  line-height: 28px;
  white-space: nowrap;
  margin: 14px 10px;
  color: ${theme.colors.gray900};
  position: relative;
  cursor: pointer;

  &:visited {
    color: ${theme.colors.gray900};
  }

  &:hover {
    color: ${theme.colors.blue600};

    .nav-link__dropdown-icon {
      > svg > path {
        fill: ${theme.colors.blue700};
      }
    }
  }

  &.nav-link--active {
    color: ${theme.colors.blue700};
  }

  .nav-link__icon {
    margin-right: 10px;
    position: relative;
    top: 4px;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .nav-link__dropdown-icon {
    display: flex;
    position: absolute;
    right: 10px;
    padding-bottom: 3px;

    ${mediaQueries.mediumScreen()} {
      position: static;
      right: unset;
    }
  }
`

const NavLink = ({
  isDropdownOpen,
  onClick,
  href,
  target,
  label,
  alt,
  className,
  icon,
  subItems,
  // eslint-disable-next-line
  tag: Tag = ({ activeClassName, partiallyActive, ...rest }) => <a {...rest} />,
  track,
  showDropdownOnClick,
  subItemsWrapper,
  onActiveLinkClick,
  ...rest
}) => {
  const isWide = useMedia({ minWidth: mediaQueries.BASE_MEDIUM_SIZE + 100 })

  const checkIfLinkIsActive = () => {
    if (typeof window !== 'undefined' && rest && rest.to) {
      return window.location.pathname.endsWith(rest.to)
    }
  }

  const handleClick = (e) => {
    if (checkIfLinkIsActive() && onActiveLinkClick) {
      onActiveLinkClick(e, { href, to: rest?.to, label })
    }

    if (onClick) {
      onClick(e, { href, to: rest?.to, label })
    }

    if (track) {
      pushGenericEvent({
        category: 'Interactions',
        action: 'Navigation',
        label,
      })
    }
  }

  return (
    <Tag
      css={navLinkCss}
      href={href}
      target={target || undefined}
      alt={alt || label}
      rel="noopener noreferrer"
      className={`nav-link ${className || ''}`}
      onClick={handleClick}
      activeClassName={label !== 'Marketplace' ? 'nav-link--active' : ''}
      {...rest}
    >
      {icon && <span className="nav-link__icon">{icon}</span>}
      <span className="nav-link__label">{label}</span>
      {subItems && (
        <span className="nav-link__dropdown-icon">
          {isWide ? (
            <ChevronIcon isOpen={isDropdownOpen} />
          ) : (
            <ChevronRight css={chevronRightCss} />
          )}
        </span>
      )}
    </Tag>
  )
}

NavLink.propTypes = {
  isDropdownOpen: bool,
  showChevron: bool,
  href: string,
  target: string,
  label: string,
  alt: string,
  className: string,
  subItems: array,
  tag: oneOfType([func, node, object]),
  icon: node,
  onClick: func,
  onActiveLinkClick: func,
  showSubItems: bool,
  showDropdownOnClick: bool,
  subItemsWrapper: func,
  track: bool,
}

export default NavLink
